import { ChangeDetectorRef, Component, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { BillableItem, ConfirmDialogModel, GenerateBillableItemForm, WorkflowStep } from '@ee/common/models';
import { BillableItemEditorDialogComponent } from './billable-item-editor-dialog.component';
import { ConfirmDialogComponent } from '@ee/common/confirm-dialog';
import { UntypedFormArray, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CurrencyPipe } from '@angular/common';

@Component({
  selector: 'ee-workflow-step-billables',
  template: `
    <div class="details mat-chips status billable-chips">
      @for (b of billableItemsArray.controls; track b; let i = $index) {
        <div class="mat-chip green billable-chip" [tp]="getTooltipText(b)" [tpDelay]="500">
          <span class="billable-amount">{{ formatAmount(b) }}</span>
          <span class="billable-description">{{ b.get('description')?.value }}</span>
          @if (workflowStep?.allow_billable_override || b.get('amount').value === 0) {
            <button
              type="button"
              class="billable-remove"
              (click)="removeBillableItem(i)"
              [attr.aria-label]="'Remove ' + b.get('description')?.value">
              <i class="fa fa-xmark"></i>
            </button>
          }
        </div>
      }
      <!-- Always available: adding a billable is never gated. allow_billable_override still gates
           REMOVING a step-defined auto-billable above (that's the workflow author's lock), but a
           step's fee list can't anticipate every cost, so adding one is always allowed. -->
      <button type="button" class="billable-add-new" (click)="addBillable()">+ Add New</button>
    </div>
  `,
  styles: [
    `
      // Content-sized pills that wrap, per the 2026-07 design cleanup — not full-width rows.
      // The mat-chips/status/green base classes are kept so the chip palette variables and the
      // dark-mode overrides in _chips.scss/_design-tokens.scss keep applying; the pill shape and
      // layout are overridden below (selectors compound the global ones so they win without
      // !important).
      .mat-chips.status.billable-chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
        padding: 2px 0 10px;
        // Never exceed the box: width:100% + min-width:0 keep the row inside its container so the
        // pills WRAP instead of laying out on one line and stretching the column (which pushed the
        // "+ Add New" pill out past the box edge). Chips are nowrap by the shared .mat-chip rule,
        // so without this the row's max-content is the sum of every pill.
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
      }

      .mat-chips.status .mat-chip.billable-chip {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        // Long descriptions (e.g. a full flat-fee clause) truncate instead of stretching the pill
        // across — and past — the box. The full text stays available via the tooltip.
        max-width: min(100%, 30rem);
        min-width: 0;
        box-sizing: border-box;
        margin: 0;
        padding: 4px 6px 4px 14px;
        min-height: 32px;
        border-radius: 9999px;
        line-height: 22px !important; // the base .mat-chip line-height is itself !important

        .billable-amount {
          font-weight: 700;
          white-space: nowrap;
          flex: 0 0 auto;
        }

        .billable-description {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .billable-remove {
        // Round "x" affordance, one shade deeper than the pill it sits in
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        flex: 0 0 auto;
        padding: 0;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        color: inherit;
        background-color: rgba(var(--chip-color-green), 0.18);
        font-size: 11px;

        &:hover {
          background-color: rgba(var(--chip-color-green), 0.32);
        }
      }

      .billable-add-new {
        // Same footprint as a billable pill (min-height + border-box), so the row reads as one
        // set of equally-sized chips rather than a chip next to a larger button.
        display: inline-flex;
        align-items: center;
        box-sizing: border-box;
        min-height: 32px;
        padding: 3px 16px; // 22px line + 6px padding + 3px border = 31px, so min-height wins at 32
        line-height: 22px;
        border: 1.5px dashed var(--color-outline, rgba(0, 0, 0, 0.24));
        border-radius: 9999px;
        background: transparent;
        color: var(--color-on-surface-variant, #616161);
        font-weight: 600;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        white-space: nowrap;
        cursor: pointer;
        user-select: none;

        &:hover {
          border-color: var(--color-on-surface-variant, #757575);
          color: var(--color-on-surface, #424242);
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class WorkflowStepBillablesComponent implements OnDestroy {
  @Input() workflowStep: WorkflowStep;
  @Input() billableItemsArray: UntypedFormArray;

  private subs: Subscription[] = [];
  private currencyPipe: CurrencyPipe;

  constructor(
    public dialog: MatDialog,
    private fb: UntypedFormBuilder,
    public changeDetectorRef: ChangeDetectorRef
  ) {
    this.currencyPipe = new CurrencyPipe('en-US');
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  formatAmount(control: AbstractControl): string {
    return this.currencyPipe.transform(control.get('amount')?.value ?? 0, 'USD', 'symbol', '1.2-2');
  }

  addBillable() {
    const dialogRef = this.dialog.open(BillableItemEditorDialogComponent, {
      // Wider so the billable item fields (esp. Description) aren't cramped; maxWidth stays at
      // Material's default 80vw so it remains responsive on narrow screens (EE-214).
      width: '720px',
      data: { title: 'Add Billable Item' }
    });
    this.subs.push(
      dialogRef.afterClosed().subscribe((billableItem: BillableItem) => {
        if (billableItem) {
          this.billableItemsArray.push(GenerateBillableItemForm(this.fb, billableItem));
        }
      })
    );
  }

  getTooltipText(control: AbstractControl): string {
    const amount = control.get('amount')?.value;
    const description = control.get('description')?.value;
    if (amount !== null && amount !== undefined && description) {
      const formattedAmount = this.currencyPipe.transform(amount, 'USD', 'symbol', '1.2-2');
      return `${formattedAmount} ${description}`;
    }
    return '';
  }

  removeBillableItem(i: number) {
    const billable = this.billableItemsArray.controls[i];
    const confirmDialog = {
      title: 'Are you sure?',
      description:
        `The billable item for <b>${billable.get('description').value}</b> in the amount ` +
        `of <b>$${billable.get('amount').value}</b> will be removed.`
    } as ConfirmDialogModel;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog });
    this.subs.push(
      dialogRef.afterClosed().subscribe((decision: boolean) => {
        if (decision) {
          this.billableItemsArray.removeAt(i);
          this.changeDetectorRef.detectChanges();
        }
      })
    );
  }
}
